import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiShield, FiExternalLink } from "react-icons/fi";
import { cguAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";

// Brique 2 — Gate de (ré)acceptation des CGU.
// Si l'utilisateur connecté n'a pas accepté la version requise (nouveau compte hérité,
// passage marchand, ou bump de version), une modale BLOQUANTE force l'acceptation.
const CGUGate = () => {
  const { user, token } = useAuth();
  const { lang, isRTL } = useLanguage();
  const ar = lang === "ar";

  const [needs, setNeeds] = useState(false);
  const [checking, setChecking] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isMerchant = user && user.role === "merchant";
  const cguPath = isMerchant ? "/cgu/marchand" : "/cgu/acheteur";

  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!token || !user || user.role === "admin") { setNeeds(false); return; }
      setChecking(true);
      try {
        const res = await cguAPI.status();
        const data = res && res.data && res.data.data ? res.data.data : (res && res.data);
        if (alive) setNeeds(!!(data && data.needsAcceptance));
      } catch (e) {
        if (alive) setNeeds(false); // ne jamais bloquer sur une erreur réseau
      } finally {
        if (alive) setChecking(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [token, user]);

  if (!needs || checking) return null;

  const txt = ar
    ? {
        title: "تحديث الشروط العامة للاستخدام",
        intro: "لمتابعة استعمال Maxula، يرجى قراءة الشروط العامة للاستخدام وقبولها.",
        read: "قراءة الشروط العامة للاستخدام",
        check: isMerchant
          ? "أقر بأنني قرأت وأقبل الشروط العامة للتجار (بما في ذلك وكالة الفوترة ومنع الوساطة الموازية)."
          : "أقر بأنني قرأت وأقبل الشروط العامة للاستخدام.",
        accept: "أقبل",
        wait: "جارٍ الحفظ…",
        err: "تعذر حفظ القبول. حاول مرة أخرى."
      }
    : {
        title: "Mise à jour des conditions d'utilisation",
        intro: "Pour continuer à utiliser Maxula, veuillez lire et accepter les conditions générales d'utilisation.",
        read: "Lire les conditions générales d'utilisation",
        check: isMerchant
          ? "Je reconnais avoir lu et j'accepte les CGU Marchand (y compris le mandat de facturation et l'anti-désintermédiation)."
          : "Je reconnais avoir lu et j'accepte les conditions générales d'utilisation.",
        accept: "J'accepte",
        wait: "Enregistrement…",
        err: "Échec de l'enregistrement. Réessayez."
      };

  const submit = async () => {
    if (!accepted || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await cguAPI.accept(isMerchant ? "merchant" : "buyer");
      setNeeds(false);
    } catch (e) {
      setError(txt.err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#fdecf5] text-[#e11d7b]">
            <FiShield size={20} />
          </span>
          <h2 className="text-lg font-bold text-gray-900">{txt.title}</h2>
        </div>

        <p className="mt-4 text-sm text-gray-600">{txt.intro}</p>

        <Link
          to={cguPath}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#e11d7b] hover:underline"
        >
          {txt.read} <FiExternalLink size={14} />
        </Link>

        <label className="mt-5 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[#e11d7b]"
          />
          <span className="text-sm text-gray-700">{txt.check}</span>
        </label>

        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}

        <button
          type="button"
          onClick={submit}
          disabled={!accepted || submitting}
          className="mt-6 w-full rounded-lg bg-[#e11d7b] py-3 font-semibold text-white hover:bg-[#c8166c] disabled:opacity-50"
        >
          {submitting ? txt.wait : txt.accept}
        </button>
      </div>
    </div>
  );
};

export default CGUGate;
